import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import DOMPurify from 'dompurify';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Container, Stack } from '../../design-system/components/Layout';
import { Heading, Text } from '../../design-system/components/Typography';
import { Badge } from '../../design-system/components/Decorative';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import { MemberFilter } from '../../components/admin/MemberFilter';
import { EmailComposer } from '../../components/admin/EmailComposer';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import FormField from '../../components/common/FormField';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { shadows } from '../../design-system/tokens/shadows';
import { animations } from '../../design-system/tokens/animations';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { MemberFilterParams, CreateEmailCampaign, EmailCampaignStatus } from '../../types/email';
import { adminService } from '../../services/admin';

const CampaignContainer = styled(Container)`
  max-width: 1400px;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: ${spacing[8]} 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.theme.colors.border};
    z-index: 0;
    border-radius: ${borders.radius.full};
  }

  ${breakpoints.down.md} {
    margin: ${spacing[4]} 0;
    gap: ${spacing[2]};

    &::before {
      display: none;
    }
  }
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => 
    props.completed 
      ? props.theme.colors.success 
      : props.active 
        ? props.theme.colors.gradientPrimary
        : props.theme.colors.surfaceHover};
  color: ${props => 
    props.completed || props.active 
      ? props.theme.colors.surface 
      : props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.lg};
  border: 3px solid ${props => 
    props.completed 
      ? props.theme.colors.success 
      : props.active 
        ? props.theme.colors.primary 
        : props.theme.colors.border};
  transition: ${animations.transition(
    ['all'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  box-shadow: ${props => 
    props.active 
      ? shadows.colored.md(props.theme.colors.primary)
      : 'none'};

  ${props => props.active && css`
    transform: scale(1.1);
  `}

  ${breakpoints.down.md} {
    width: 36px;
    height: 36px;
    font-size: ${typography.fontSize.sm};
    border-width: 2px;
  }
`;

const StepLabel = styled(Text)<{ active: boolean }>`
  margin-top: ${spacing[3]};
  font-weight: ${props => props.active ? typography.fontWeight.semibold : typography.fontWeight.normal};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  text-align: center;

  ${breakpoints.down.md} {
    display: none;
  }
`;

const MobileStepSummary = styled(Text)`
  display: none;
  margin: 0 0 ${spacing[4]} 0;
  text-align: center;

  ${breakpoints.down.md} {
    display: block;
  }
`;

const StepContent = styled.div`
  margin: ${spacing[8]} 0;
`;

const ActionsRow = styled(Stack)`
  margin-top: ${spacing[8]};
  padding-top: ${spacing[8]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
`;

const HeadingWithMarginBottom = styled(Heading)`
  margin-bottom: ${spacing[8]};
`;

const ReviewCard = styled(Card)`
  padding: ${spacing[8]};
  position: relative;
`;

const ReviewSection = styled(Stack)`
  margin-bottom: ${spacing[6]};
`;

const ReviewLabel = styled(Text).attrs(() => ({
  size: 'xs' as const,
}))`
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-weight: ${typography.fontWeight.semibold};
  margin-bottom: ${spacing[2]};
`;

const ReviewValue = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
  border-left: 4px solid ${props => props.theme.colors.primary};
`;

const HeaderCard = styled(Card)`
  padding: ${spacing[8]};
  position: relative;
  overflow: visible;
`;

const EmailCampaign: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { success: showSuccess, error: showError } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [filters, setFilters] = useState<MemberFilterParams>({});
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjectEn, setSubjectEn] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [memberCount, setMemberCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [campaignNameError, setCampaignNameError] = useState<string | null>(null);
  const [composerForceLanguage, setComposerForceLanguage] = useState<'gr' | 'en' | null>(null);
  const [composerFieldErrors, setComposerFieldErrors] = useState<{
    subject?: string;
    body?: string;
    subjectEn?: string;
    bodyEn?: string;
  }>({});

  React.useEffect(() => {
    // Update member count when filters change
    const updateMemberCount = async () => {
      try {
        const result = await adminService.getFilteredMembers({
          ...filters,
          page: 1,
          pageSize: 1,
        });
        setMemberCount(result.total);
      } catch (error) {
        console.error('Error getting member count:', error);
      }
    };
    updateMemberCount();
  }, [filters]);

  const handleNext = () => {
    if (currentStep === 1) {
      if (memberCount === 0) {
        showError(t('email.noMembersSelected'));
        return;
      }
      setCampaignNameError(null);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCampaignNameError(null);
      setComposerFieldErrors({});
      setComposerForceLanguage(null);

      if (!campaignName.trim()) {
        setCampaignNameError(t('email.campaignNameRequired'));
        showError(t('email.campaignNameRequired'));
        document.getElementById('email-campaign-name')?.focus();
        return;
      }

      const missingGreek = !subject.trim() || !body.trim();
      const missingEnglish = !subjectEn.trim() || !bodyEn.trim();
      if (missingGreek || missingEnglish) {
        const errors: {
          subject?: string;
          body?: string;
          subjectEn?: string;
          bodyEn?: string;
        } = {};
        if (!subject.trim()) errors.subject = t('email.greekContentRequired');
        if (!body.trim()) errors.body = t('email.greekContentRequired');
        if (!subjectEn.trim()) errors.subjectEn = t('email.englishContentRequired');
        if (!bodyEn.trim()) errors.bodyEn = t('email.englishContentRequired');
        setComposerFieldErrors(errors);
        if (missingEnglish) {
          setComposerForceLanguage('en');
          showError(t('email.englishContentRequired'));
        } else {
          setComposerForceLanguage('gr');
          showError(t('email.greekContentRequired'));
        }
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCampaignNameError(null);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) {
      showError(t('email.campaignNameRequired'));
      return;
    }

    setCreating(true);
    try {
      const campaign: CreateEmailCampaign = {
        name: campaignName,
        subject,
        body,
        subjectEn,
        bodyEn,
        memberFilter: filters,
        templateId,
      };

      const created = await adminService.createEmailCampaign(campaign);
      setCampaignId(created.id);
      setShowConfirmDialog(true);
    } catch (error) {
      console.error('Error creating campaign:', error);
      showError(t('email.errorCreatingCampaign'));
    } finally {
      setCreating(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!campaignId) return;

    setSending(true);
    try {
      await adminService.sendEmailCampaign(campaignId);
      showSuccess(t('email.campaignSent'));
      navigate('/admin/email/history');
    } catch (error) {
      console.error('Error sending campaign:', error);
      showError(t('email.errorSendingCampaign'));
    } finally {
      setSending(false);
      setShowConfirmDialog(false);
    }
  };

  const steps = [
    { number: 1, label: t('email.filterMembers') },
    { number: 2, label: t('email.composeEmail') },
    { number: 3, label: t('email.reviewAndSend') },
  ];

  return (
    <CampaignContainer>
      <PageHeader
        title={t('email.newCampaign')}
        subtitle={t('email.campaignSubtitle')}
        pattern={true}
      />

      <HeaderCard variant="elevated" headerVariant="gradient" accentBorder="top">
        <MobileStepSummary size="sm" weight="semibold" color="primary">
          {t('email.stepProgress', {
            current: currentStep,
            total: steps.length,
            label: steps.find(s => s.number === currentStep)?.label ?? '',
            defaultValue: `Step {{current}} of {{total}}: {{label}}`,
          })}
        </MobileStepSummary>
        <StepsContainer>
          {steps.map((step) => (
            <Step
              key={step.number}
              active={currentStep === step.number}
              completed={currentStep > step.number}
            >
              <StepCircle
                active={currentStep === step.number}
                completed={currentStep > step.number}
              >
                {currentStep > step.number ? '✓' : step.number}
              </StepCircle>
              <StepLabel 
                active={currentStep === step.number}
                size="sm"
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </StepsContainer>
      </HeaderCard>

      <StepContent>
        {currentStep === 1 && (
          <MemberFilter filters={filters} onFiltersChange={setFilters} />
        )}

        {currentStep === 2 && (
          <Stack direction="column" spacing="lg">
            <Card variant="elevated" accentBorder="top" pattern="dots">
              <FormField
                id="email-campaign-name"
                name="campaignName"
                label={t('email.campaignName')}
                value={campaignName}
                onChange={(e) => {
                  setCampaignName(e.target.value);
                  if (campaignNameError) setCampaignNameError(null);
                }}
                error={campaignNameError || undefined}
                helperText={
                  campaignNameError
                    ? undefined
                    : t('email.campaignNameHint', {
                        defaultValue: 'Used as the internal name in campaign history.',
                      })
                }
                placeholder={t('email.campaignNamePlaceholder')}
              />
            </Card>
            <EmailComposer
              subject={subject}
              body={body}
              subjectEn={subjectEn}
              bodyEn={bodyEn}
              onSubjectChange={v => {
                setSubject(v);
                setComposerFieldErrors(prev => ({ ...prev, subject: undefined }));
              }}
              onBodyChange={v => {
                setBody(v);
                setComposerFieldErrors(prev => ({ ...prev, body: undefined }));
              }}
              onSubjectEnChange={v => {
                setSubjectEn(v);
                setComposerFieldErrors(prev => ({ ...prev, subjectEn: undefined }));
              }}
              onBodyEnChange={v => {
                setBodyEn(v);
                setComposerFieldErrors(prev => ({ ...prev, bodyEn: undefined }));
              }}
              templateId={templateId}
              onTemplateChange={setTemplateId}
              forceLanguage={composerForceLanguage}
              fieldErrors={composerFieldErrors}
            />
          </Stack>
        )}

        {currentStep === 3 && (
          <ReviewCard variant="elevated" headerVariant="gradient" accentBorder="top">
            <HeadingWithMarginBottom variant="h2" color="text">
              {t('email.reviewCampaign')}
            </HeadingWithMarginBottom>

            <ReviewSection direction="column" spacing="lg">
              <div>
                <ReviewLabel>{t('email.campaignName')}</ReviewLabel>
                <ReviewValue>
                  <Text size="lg" weight="semibold">{campaignName}</Text>
                </ReviewValue>
              </div>

              <div>
                <ReviewLabel>{t('email.recipients')}</ReviewLabel>
                <ReviewValue>
                  <Badge size="lg" color="primary">
                    {memberCount} {t('email.members')}
                  </Badge>
                </ReviewValue>
              </div>

              <div>
                <ReviewLabel>{t('email.subject')} ({t('email.languageGreek')})</ReviewLabel>
                <ReviewValue>
                  <Text size="base" weight="medium">{subject}</Text>
                </ReviewValue>
              </div>

              <div>
                <ReviewLabel>{t('email.body')} ({t('email.languageGreek')})</ReviewLabel>
                <ReviewValue>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(body),
                    }}
                  />
                </ReviewValue>
              </div>

              <div>
                <ReviewLabel>{t('email.subject')} ({t('email.languageEnglish')})</ReviewLabel>
                <ReviewValue>
                  <Text size="base" weight="medium">{subjectEn}</Text>
                </ReviewValue>
              </div>

              <div>
                <ReviewLabel>{t('email.body')} ({t('email.languageEnglish')})</ReviewLabel>
                <ReviewValue>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(bodyEn),
                    }}
                  />
                </ReviewValue>
              </div>
            </ReviewSection>
          </ReviewCard>
        )}
      </StepContent>

      <ActionsRow direction="row" justify="space-between" align="center">
        <div>
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack} glow>
              {t('email.back')}
            </Button>
          )}
        </div>
        <Stack direction="row" spacing="md">
          {currentStep < 3 ? (
            <Button variant="primary" onClick={handleNext} glow ripple>
              {t('email.next')} →
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleBack} glow>
                {t('email.back')}
              </Button>
              <Button
                variant="gradient"
                onClick={campaignId ? () => setShowConfirmDialog(true) : handleCreateCampaign}
                disabled={!campaignName.trim() || creating || sending}
                glow
                ripple
                loading={creating || sending}
              >
                {campaignId
                  ? t('email.sendCampaign')
                  : t('email.createCampaign')}
              </Button>
            </>
          )}
        </Stack>
      </ActionsRow>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleSendCampaign}
        title={t('email.confirmSend')}
        message={t('email.confirmSendMessage') || `Are you sure you want to send this campaign to ${memberCount} members?`}
        confirmText={t('email.send')}
        cancelText={t('email.cancel')}
        variant="primary"
      />
    </CampaignContainer>
  );
};

export default EmailCampaign;
